import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../services/ApiService";
import OrderStatusPopup from "./orderStatusPopup";
import "./replaceRequests.css";

const STATUS_OPTIONS = [
  { value: "all", label: "All Requests" },
  { value: "requested", label: "Requested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "pickup_scheduled", label: "Pickup Scheduled" },
  { value: "picked_up", label: "Picked Up" },
  { value: "replacement_in_progress", label: "In Progress" },
  { value: "replaced", label: "Replaced" },
];

export default function ReplaceRequests() {
  const [replacementOrders, setReplacementOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  useEffect(() => {
    fetchReplacementOrders();
  }, []);

  const fetchReplacementOrders = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get("/replacements");
      setReplacementOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching replacement orders:", error);
      setReplacementOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (updatedOrder) => {
    try {
      setUpdatingId(updatedOrder.id);
      await ApiService.put(`/replacement-orders/${updatedOrder.id}`, updatedOrder);
      await fetchReplacementOrders();
      setShowModal(false);
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return replacementOrders.filter((order) => {
      const matchesStatus =
        filterStatus === "all" || order.status === filterStatus;

      const matchesSearch =
        !q ||
        String(order.orderItemId || "").toLowerCase().includes(q) ||
        String(order.name || "").toLowerCase().includes(q) ||
        String(order.phoneNumber || "").toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [replacementOrders, filterStatus, searchQuery]);

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredOrders.slice(start, end);
  }, [filteredOrders, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchQuery, rowsPerPage]);

  const stats = useMemo(() => {
    return {
      total: replacementOrders.length,
      requested: replacementOrders.filter((o) => o.status === "requested").length,
      approved: replacementOrders.filter((o) => o.status === "approved").length,
      replaced: replacementOrders.filter(
        (o) => o.status === "replaced" || o.status === "completed"
      ).length,
    };
  }, [replacementOrders]);

  const statusClass = (status) => {
    switch (status) {
      case "requested":
        return "status-requested";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "pickup_scheduled":
      case "picked_up":
      case "replacement_in_progress":
        return "status-progress";
      case "completed":
      case "replaced":
        return "status-replaced";
      default:
        return "status-default";
    }
  };

  const formatStatus = (status) =>
    status ? status.replaceAll("_", " ") : "unknown";

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "-";

  return (
    <div className="replace-requests-page">
      <div className="replace-requests-hero">
        <div>
          <p className="replace-requests-eyebrow">Service Management</p>
          <h2 className="replace-requests-title">Replacement Requests</h2>
          {/* <p className="replace-requests-subtitle">
            Track, filter and manage customer replacement requests.
          </p> */}
        </div>

        <button
          className="replace-refresh-btn"
          onClick={fetchReplacementOrders}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="replace-stats-grid">
        <button
          type="button"
          className={`replace-stat-card ${filterStatus === "all" ? "active" : ""}`}
          onClick={() => setFilterStatus("all")}
        >
          <span>Total Requests</span>
          <strong>{stats.total}</strong>
        </button>

        <button
          type="button"
          className={`replace-stat-card requested ${filterStatus === "requested" ? "active" : ""
            }`}
          onClick={() => setFilterStatus("requested")}
        >
          <span>Requested</span>
          <strong>{stats.requested}</strong>
        </button>

        <button
          type="button"
          className={`replace-stat-card approved ${filterStatus === "approved" ? "active" : ""
            }`}
          onClick={() => setFilterStatus("approved")}
        >
          <span>Approved</span>
          <strong>{stats.approved}</strong>
        </button>

        <button
          type="button"
          className={`replace-stat-card replaced ${filterStatus === "replaced" ? "active" : ""
            }`}
          onClick={() => setFilterStatus("replaced")}
        >
          <span>Replaced</span>
          <strong>{stats.replaced}</strong>
        </button>
      </div>

      <div className="replace-panel">
        <div className="replace-toolbar">
          <div className="replace-search-wrap">
            <span>⌕</span>
            <input
              type="text"
              placeholder="Search by order ID, customer or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="replace-requests-search"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="replace-requests-select"
          >
            {STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="replace-requests-table-wrapper">
          <table className="replace-requests-table">
            <thead>
              <tr>
                <th>Order Item</th>
                <th>Product Details</th>
                <th>Reason</th>
                <th>Requested</th>
                {/* <th>Images</th> */}
                <th>Status</th>
                <th className="replace-actions-th">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="replace-empty-cell">
                    Loading replacement requests...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="replace-empty-cell">
                    No replacement requests found.
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order) => {
                  const item = order.orderItem || {};

                  return (
                    <tr key={order.id}>
                      <td>
                        <div className="replace-order-block">
                          <span className="replace-order-id">#{order.orderItemId}</span>
                          <small>{item.orderItemNumber || "-"}</small>
                        </div>
                      </td>

                      <td>
                        <div className="replace-product">
                          <strong>{item.deviceName || "-"}</strong>
                          {/* <span>
                            Qty: {item.quantity || "-"} • Network: {item.networkType || "-"}
                          </span>
                          <span>Total: ₹{Number(item.totalPrice || 0).toFixed(2)}</span> */}
                        </div>
                      </td>

                      <td>
                        <div className="replace-reason">
                          <strong>{order.reason || "-"}</strong>
                          {/* <span>{order.comment || "-"}</span> */}
                        </div>
                      </td>

                      <td>
                        <div className="replace-date-block">
                          <strong>{formatDate(order.createdAt)}</strong>
                          <span>pickup: {formatDate(order.pickupDate)}</span>
                        </div>
                      </td>

                      {/* <td>
                        <div className="replace-images">
                          {order.images?.length > 0 ? (
                            order.images.slice(0, 2).map((img, index) => (
                              <a key={index} href={img} target="_blank" rel="noreferrer">
                                <img src={img} alt="replacement proof" />
                              </a>
                            ))
                          ) : (
                            <span className="replace-no-image">No image</span>
                          )}
                        </div>
                      </td> */}

                      <td>
                        <span className={`replace-status-badge ${statusClass(order.status)}`}>
                          {formatStatus(order.status)}
                        </span>
                      </td>

                      <td>
                        <div className="replace-actions">
                          {order.status === "approved" && (
                            <button
                              className="replace-action-btn warning"
                              disabled={updatingId === order.id}
                              onClick={() =>
                                handleStatusUpdate({
                                  ...order,
                                  status: "pickup_scheduled",
                                })
                              }
                            >
                              Schedule Pickup
                            </button>
                          )}

                          {order.status === "picked_up" && (
                            <button
                              className="replace-action-btn success"
                              disabled={updatingId === order.id}
                              onClick={() =>
                                handleStatusUpdate({
                                  ...order,
                                  status: "replaced",
                                })
                              }
                            >
                              Mark Replaced
                            </button>
                          )}

                          <button
                            className="replace-action-btn primary"
                            onClick={() => navigate(`/order-item/${order.orderItemId}`)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {filteredOrders.length > 0 && (
          <div className="replace-pagination">
            <div className="replace-pagination-info">
              Showing{" "}
              <strong>{(currentPage - 1) * rowsPerPage + 1}</strong>
              {" "}to{" "}
              <strong>{Math.min(currentPage * rowsPerPage, filteredOrders.length)}</strong>
              {" "}of <strong>{filteredOrders.length}</strong> requests
            </div>

            <div className="replace-pagination-controls">
              <select
                className="replace-pagination-select"
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>

              <button
                className="replace-pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              <span className="replace-pagination-page">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="replace-pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <OrderStatusPopup
          order={selectedOrder}
          onClose={() => setShowModal(false)}
          onUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
}













// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import ApiService, { initialAuthState } from '../../services/ApiService';
// import OrderStatusPopup from './orderStatusPopup';
// import './replaceRequests.css';

// export default function ReplaceRequests() {
//   const [replacementOrders, setReplacementOrders] = useState([]);
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [showModal, setShowModal] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchReplacementOrders();
//   }, []);

//   const fetchReplacementOrders = async () => {
//     try {
//       const response = await ApiService.post('/Refund/getRefundDetails', {
//         companyCode: initialAuthState.companyCode,
//         unitCode: initialAuthState.unitCode,
//       });

//       if (response.status) {
//         setReplacementOrders(response.data);
//       } else {
//         alert('Error fetching refund requests');
//       }
//     } catch (error) {
//       console.error('Error fetching refund details:', error);
//       alert('Something went wrong.');
//     }
//   };

//   const handleStatusClick = (refund) => {
//     setSelectedOrder(refund);
//     setShowModal(true);
//   };

//   const handleStatusUpdate = async (updatedRefund) => {
//     // You can implement update logic here if needed
//     fetchReplacementOrders();
//     setShowModal(false);
//   };

//   return (
//     <div className="replace-requests-container">
//       <h2 className="replace-requests-title">Replacement Requests</h2>

//       <div className="replace-requests-table-wrapper">
//         <table className="replace-requests-table">
//           <thead>
//             <tr className="replace-requests-table-head-row">
//               <th className="replace-requests-th">Order ID</th>
//               <th className="replace-requests-th">Customer Name</th>
//               <th className="replace-requests-th">Request Date</th>
//               <th className="replace-requests-th">Status</th>
//               <th className="replace-requests-th">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {replacementOrders.map((refund) => (
//               <tr key={refund.id} className="replace-requests-row">
//                 <td className="replace-requests-td">{refund.orderId}</td>
//                 <td className="replace-requests-td">{refund.name}</td>
//                 <td className="replace-requests-td">
//                   {new Date(refund.dateOfRequest).toLocaleDateString()}
//                 </td>
//                 <td className="replace-requests-td">
//                   <button
//                     onClick={() => handleStatusClick(refund)}
//                     className="replace-requests-status-button"
//                   >
//                     {refund.refundStatus}
//                   </button>
//                 </td>
//                 <td className="replace-requests-td">
//                   <button
//                     className="replace-requests-view-button"
//                     onClick={() => navigate(`/order-details/${refund.orderId}`)}
//                   >
//                     View
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {showModal && (
//         <OrderStatusPopup
//           order={selectedOrder}
//           onClose={() => setShowModal(false)}
//           onUpdate={handleStatusUpdate}
//         />
//       )}
//     </div>
//   );
// }
